import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useCheckFlag } from '@speeki/feature-flags';
import {
    Button,
    ModalComponent,
    ModalProps,
} from '@speeki/global-ui-components';
import { Modal } from '@speeki/global-ui-components';

import {
    CHANGE_PERSONAL_INFO_FORM_ID,
    ChangePersonalInfoForm,
} from '@components/organisms/ChangePersonalInfoForm/ChangePersonalInfoForm';
import {
    PERSONAL_INFO_FORM_ID,
    PersonalInfoForm,
} from '@components/organisms/PersonalInfoForm/PersonalInfoForm';

import { RespondentTypeDocument } from '@graphql/generated/graphql';

import { useModalOverflow } from '@utils/hooks/useModalOverflow';

import { useQuery, useReactiveVar } from '@apollo/client';
import { personalInfoDisclosureUuidVar } from '@boot/reactiveVariables';

interface ChangePersonalInfoModalProps
    extends Pick<ModalProps, 'onClose' | 'open'> {}

export const ChangePersonalInfoModal = ({
    onClose,
    open,
}: ChangePersonalInfoModalProps) => {
    const [isDisabled, setIsDisabled] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    const isEngagementFeatureEnabled = useCheckFlag(
        'client_admin-stakeholder_engagement-20250312',
    );

    const disclosureUuid = useReactiveVar(
        personalInfoDisclosureUuidVar,
    ) as string;

    const { t } = useTranslation();

    useModalOverflow(open);

    const { data: respondentTypeData } = useQuery(RespondentTypeDocument, {
        variables: {
            disclosureUuid,
        },
    });

    const respondentType = respondentTypeData?.details.respondentType;
    const isOrganization = respondentType === 'ORGANIZATION';

    return (
        <ModalComponent closeButton onClose={onClose} open={open} width={648}>
            <Modal.Content variant="secondary">
                <Modal.Header>
                    <Modal.TitleSecondary center>
                        {t('PERSONAL_INFORMATION')}
                    </Modal.TitleSecondary>
                    <Modal.Description>
                        {isOrganization
                            ? t('PERSONAL_INFORMATION_TEXT_ORGANISATION')
                            : t('PERSONAL_INFORMATION_TEXT_INDIVIDUAL')}
                    </Modal.Description>
                </Modal.Header>

                {isEngagementFeatureEnabled ? (
                    <PersonalInfoForm
                        callback={onClose}
                        disclosureUuid={disclosureUuid}
                        isOrganization={isOrganization}
                        setIsDisabled={setIsDisabled}
                        setIsLoading={setIsLoading}
                    />
                ) : (
                    <ChangePersonalInfoForm
                        callback={onClose}
                        disclosureUuid={disclosureUuid}
                        setIsDisabled={setIsDisabled}
                        setIsLoading={setIsLoading}
                    />
                )}
            </Modal.Content>

            <Modal.Footer>
                <Modal.ButtonsWrapper>
                    <Button
                        disabled={isLoading}
                        onClick={onClose}
                        testId="button_cancel"
                        type="button"
                        variant={'secondary'}
                    >
                        {t('CANCEL')}
                    </Button>
                    <Button
                        disabled={isDisabled}
                        form={
                            isEngagementFeatureEnabled
                                ? PERSONAL_INFO_FORM_ID
                                : CHANGE_PERSONAL_INFO_FORM_ID
                        }
                        isLoading={isLoading}
                        testId="button_continue"
                        type="submit"
                        variant={'primary'}
                    >
                        {t('CONTINUE')}
                    </Button>
                </Modal.ButtonsWrapper>
            </Modal.Footer>
        </ModalComponent>
    );
};
